<template>
    <TransitionRoot
        as="template"
        :show="open"
        :unmount="unmountWhenHidden"
        @after-leave="afterLeave"
    >
        <Dialog
            as="div"
            :initial-focus="defaultFocus"
            static
            class="fixed inset-0 z-50 mx-auto"
            :class="viewportScroll ? 'overflow-y-auto' : 'overflow-y-hidden lg:overflow-visible'"
            :open="open"
            @close="handleDialogClose"
        >
            <div
                class="muval-modal-viewport-shell inset-x-0 bottom-0 mx-auto flex items-end justify-center bg-white text-center sm:block md:absolute md:bg-transparent md:px-8 md:pt-4 md:pb-20 lg:relative lg:min-h-screen"
                :class="[shellSize, { 'md:px-0! md:pt-0! md:pb-0! lg:px-8! lg:pt-4! lg:pb-20!': fullscreenOnTablet }]"
            >
                <TransitionChild
                    as="template"
                    :unmount="unmountWhenHidden"
                    enter="ease-out duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <DialogOverlay class="fixed inset-0 bg-gray-500/75 transition-opacity" />
                </TransitionChild>
                <!-- This element is to trick the browser into centering the modal contents. -->
                <span
                    v-if="!top"
                    class="hidden sm:inline-block sm:h-screen sm:align-middle"
                    :class="{ 'md:hidden! lg:inline-block!': fullscreenOnTablet }"
                    aria-hidden="true"
                >
                    &#8203;
                </span>
                <TransitionChild
                    as="template"
                    :unmount="unmountWhenHidden"
                    enter="ease-out duration-300"
                    enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100 translate-y-0 sm:scale-100"
                    leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <DialogPanel as="template">
                        <div
                            ref="modalRef"
                            class="shadow-muval-3 md:rounded-muval-2 md:rounded-b-muval-2 relative z-10 inline-block h-full w-full overflow-hidden overflow-y-visible rounded-b-none bg-white text-left align-bottom transition-all sm:my-8 sm:max-w-screen sm:align-middle md:h-auto dark:bg-gray-800"
                            :class="[
                                modalSize ? modalSize
                                : isComponent == 'DeleteModal' ? 'md:w-2/3'
                                : 'md:w-full',
                                isDragging ? 'sm:cursor-grabbing' : '',
                                fullscreenOnTablet ?
                                    'lg:rounded-muval-2! lg:rounded-b-muval-2! md:my-0! md:h-full! md:rounded-none! lg:my-8! lg:h-auto!'
                                :   '',
                            ]"
                            :style="modalStyle"
                        >
                            <div
                                class="flex h-full min-h-0 flex-col bg-white px-4 pt-5 pb-4 sm:p-6 md:h-auto md:bg-transparent dark:bg-gray-700"
                                :class="{ 'md:h-full! lg:h-auto!': fullscreenOnTablet }"
                            >
                                <div
                                    class="mb-2 flex h-12 items-center"
                                    :class="{ 'cursor-grabbing': isDragging }"
                                    @mousedown="startDrag"
                                >
                                    <h3
                                        v-show="hasHeaderSlot"
                                        class="flex items-center dark:border-gray-600"
                                    >
                                        <slot name="header"></slot>
                                    </h3>
                                    <DialogTitle
                                        v-if="title"
                                        as="h3"
                                        class="text-lg font-medium"
                                    >
                                        {{ title }}
                                    </DialogTitle>
                                    <DialogTitle
                                        v-if="isComponent == 'DeleteModal'"
                                        as="span"
                                        class="flex items-center gap-2.5 text-base font-medium"
                                    >
                                        <SvgIcon
                                            name="system-error"
                                            class="text-error h-6 w-6"
                                        />
                                        Are you sure?
                                    </DialogTitle>
                                    <button
                                        v-if="closeButton"
                                        id="closeButton"
                                        type="button"
                                        class="ml-auto flex h-8 w-8 items-center justify-center rounded-full bg-white hover:bg-gray-100 focus:outline-hidden dark:bg-gray-800"
                                        @click="handleCloseButton"
                                    >
                                        <SvgIcon
                                            name="system-close"
                                            class="h-5 w-5 text-gray-900 dark:text-white"
                                        />
                                    </button>
                                </div>
                                <div
                                    class="font-body flex min-h-0 flex-1 flex-col dark:text-white"
                                    :class="{
                                        'pb-8 md:pb-0': hasFooterSlot,
                                    }"
                                >
                                    <Suspense>
                                        <div
                                            :key="contentKey"
                                            class="flex min-h-0 flex-1 flex-col"
                                        >
                                            <component
                                                v-bind="{
                                                    data: data,
                                                    url: url,
                                                    parentForm: parentForm,
                                                    itemId: itemId,
                                                    itemIndex: itemIndex,
                                                    itemType: itemType,
                                                    deleteItemSchema: deleteItemSchema,
                                                    isCreate: isCreate,
                                                    isAdd: isAdd,
                                                    emitKey: emitKey,
                                                    isDefaultMessage: isDefaultMessage,
                                                    customMessage: customMessage,
                                                    customConfirmButtonText: customConfirmButtonText,
                                                    customerEmail: customerEmail,
                                                    restorable: restorable,
                                                    deleteParams: deleteParams,
                                                }"
                                                :is="isComponent"
                                                v-if="isComponent"
                                                :scope="$props.scope"
                                                @remove:item="removeItem"
                                                @close:modal="closeModal"
                                                @cancel:modal="emit('update:modelValue', false)"
                                            />
                                            <slot
                                                v-else
                                                @close:modal="closeModal"
                                            />
                                        </div>
                                        <template #fallback>
                                            <div class="flex flex-col space-y-4">
                                                <div
                                                    v-for="idx in 5"
                                                    :key="idx"
                                                    class="bg-muval-gray-5 h-12 w-full"
                                                ></div>
                                            </div>
                                        </template>
                                    </Suspense>
                                </div>
                            </div>
                            <div
                                v-show="hasFooterSlot"
                                class="border-t px-4 py-3 sm:px-6 dark:border-gray-600 dark:text-white"
                                :class="plainFooter ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800'"
                            >
                                <slot name="footer"></slot>
                            </div>
                            <ModalDiscardConfirm
                                :open="confirmOpen"
                                @keep="keepEditing"
                                @discard="confirmDiscard"
                            />
                        </div>
                    </DialogPanel>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import { Dialog, DialogOverlay, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { useRetainedModalContent } from '@composables/useRetainedModalContent';
import ModalDiscardConfirm from './ModalDiscardConfirm.vue';

// Modal Templates
import AddInterstatePrice from './templates/AddInterstatePrice.vue';
import EditInterstatePrice from './templates/EditInterstatePrice.vue';
import DeleteModal from '@components/modal/templates/DeleteModal.vue';
import RestoreModal from '@components/modal/templates/RestoreModal.vue';
import EmailModal from '@components/modal/templates/EmailModal.vue';
import EmailInvoiceModal from '@components/modal/templates/EmailInvoiceModal.vue';
import SmsModal from '@components/modal/templates/SmsModal.vue';
import ArchiveModal from '@components/modal/templates/ArchiveModal.vue';
import AssignAgent from '@components/modal/templates/AssignAgent.vue';
import AssignInboxOrder from '@components/modal/templates/AssignInboxOrder.vue';
import DeleteInboxEmail from '@components/modal/templates/DeleteInboxEmail.vue';

export default {
    components: {
        Dialog,
        DialogOverlay,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        ModalDiscardConfirm,

        // Modals
        AddInterstatePrice,
        EditInterstatePrice,
        DeleteModal,
        RestoreModal,
        EmailModal,
        EmailInvoiceModal,
        SmsModal,
        ArchiveModal,
        AssignAgent,
        AssignInboxOrder,
        DeleteInboxEmail,
    },
    props: {
        modelValue: {
            type: Boolean,
            required: true,
        },
        title: {
            type: String,
            required: false,
            default: '',
        },
        closeButton: {
            type: Boolean,
            required: false,
            default: true,
        },

        // Drops the footer's grey fill in favour of white, per the style guide.
        // Opt-in rather than the default: 25 modals currently rely on the grey,
        // and none of them have been reviewed against the guide.
        plainFooter: {
            type: Boolean,
            required: false,
            default: false,
        },
        focusRef: {
            type: [Object, null],
            required: false,
            default: null,
        },
        top: {
            type: Boolean,
            default: false,
        },
        isComponent: {
            type: [Object, String, null],
            required: false,
            default: null,
        },
        data: {
            type: [Object, null],
            required: false,
            default: null,
        },

        // Which url to use for the component
        url: {
            type: [String, null],
            required: false,
            default: null,
        },

        // Gives modal or it's dynamic component access to passed form useForm()
        parentForm: {
            type: [Function, null],
            required: false,
            default: null,
        },

        // Used to identify if the context is being created
        isCreate: {
            type: Boolean,
            required: false,
            default: false,
        },

        // Used to identify whether the edit should be in an "Add" view
        isAdd: {
            type: Boolean,
            required: false,
            default: false,
        },

        // Used to identify the item being edited or deleted
        itemId: {
            type: [Number, null],
            required: false,
            default: null,
        },
        itemIndex: {
            type: [Number, null],
            required: false,
            default: null,
        },
        itemType: {
            type: [String, null],
            required: false,
            default: null,
        },

        // Schema used to customize what delete reference data is shown in the DeleteModal
        deleteItemSchema: {
            type: [Array, null],
            required: false,
            default: null,
        },
        emitKey: {
            type: [String, null],
            required: false,
            default: null,
        },
        modalSize: {
            type: [String, null],
            required: false,
            default: null,
        },
        // Max width of the outer viewport shell that clamps the modal. Defaults
        // to the historical `max-w-5xl`; pass a larger Tailwind max-width class
        // (e.g. `max-w-7xl`) for content-heavy modals that would otherwise overflow.
        shellSize: {
            type: String,
            required: false,
            default: 'max-w-5xl',
        },
        scope: {
            type: String,
            required: false,
            default: 'tenant',
        },
        isDefaultMessage: {
            type: Boolean,
            required: false,
            default: true,
        },
        customMessage: {
            type: String,
            required: false,
            default: null,
        },
        customConfirmButtonText: {
            type: String,
            required: false,
            default: null,
        },
        closeOnOutside: {
            type: Boolean,
            required: false,
            default: true,
        },
        // Retention (CU-86d3wvwq7): click-outside hides the modal and keeps its contents mounted;
        // the X becomes the only discard control and warns when there are unsaved changes.
        retainOnDismiss: {
            type: Boolean,
            required: false,
            default: false,
        },
        // Identity of the record the retained contents belong to; a change discards them.
        retainKey: {
            type: [String, Number, null],
            required: false,
            default: null,
        },
        // Dirty check for a host that owns the form outside this modal's slot.
        isDirty: {
            type: [Function, null],
            required: false,
            default: null,
        },
        viewportScroll: {
            type: Boolean,
            required: false,
            default: true,
        },
        fullscreenOnTablet: {
            type: Boolean,
            required: false,
            default: false,
        },
        customerEmail: {
            type: String,
            required: false,
            default: '',
        },
        /**
         * Whether the deleted record can be brought back, which decides whether `DeleteModal`
         * offers an Undo action on its success toast.
         *
         * Defaults to true because most delete surfaces have a `restore` endpoint. The non-move
         * services do not — packing, unpacking, packing materials, car transport and pet transport
         * all offer a delete with no route to undo it — so those pass false rather than showing an
         * action that can only 404 (CU-86d45pvyd, Jay's review).
         */
        restorable: {
            type: Boolean,
            required: false,
            default: true,
        },
        /** Query parameters `DeleteModal` sends with its DELETE, such as a draft invoice row's `{ draft: 1 }`. */
        deleteParams: {
            type: [Object, null],
            required: false,
            default: null,
        },
    },
    emits: [
        'update:modelValue',
        'remove:item',
        'success',
        'modal:cancel',
        'email:sent',
        'modal:dismiss',
        'modal:discard',
    ],
    setup(props, { emit, slots }) {
        const defaultFocus = ref(null);
        const modalRef = ref(null);

        const retained = useRetainedModalContent({
            open: () => props.modelValue,
            setOpen: (value) => emit('update:modelValue', value),
            cancel: handleCancel,
            retainOnDismiss: () => props.retainOnDismiss,
            retainKey: () => props.retainKey,
            isDirty: () => props.isDirty,
            emit,
        });

        // Drag and drop functionality
        const isDragging = ref(false);
        const dragStart = ref({ x: 0, y: 0 });
        const position = ref({ x: 0, y: 0 });
        const initialModalTop = ref(0);

        const hasHeaderSlot = computed(() => {
            return !!slots.header;
        });

        // Modal style
        const modalStyle = computed(() => {
            if (position.value.x === 0 && position.value.y === 0) {
                return {};
            }
            return {
                transform: `translate(${position.value.x}px, ${position.value.y}px)`,
                transition: isDragging.value ? 'none' : 'transform 0.3s ease-out',
            };
        });

        function closeModal() {
            emit('update:modelValue', false);
            emit('success');

            // Reset position when modal closes
            position.value = { x: 0, y: 0 };
        }

        function removeItem(event) {
            emit('remove:item', event);
        }

        function handleCancel() {
            emit('modal:cancel');
            emit('update:modelValue', false);

            // Reset position when modal closes
            position.value = { x: 0, y: 0 };
        }

        // Handle Dialog backdrop / escape close events.
        function handleDialogClose() {
            if (!props.closeOnOutside) return;
            if (props.retainOnDismiss) {
                handleDismiss();
                return;
            }
            handleCancel();
        }

        // Click-outside / Escape on a retaining modal: hide it, keep the contents.
        function handleDismiss() {
            retained.dismiss();
            position.value = { x: 0, y: 0 };
        }

        // The X: on a retaining modal it confirms when dirty and discards otherwise.
        function handleCloseButton() {
            if (!props.retainOnDismiss) {
                handleCancel();
                return;
            }
            retained.requestClose();
        }

        const hasCustomHeaderSlot = computed(() => {
            return !!slots.customHeader;
        });

        // Start dragging the modal
        function startDrag(event) {
            if (event.type === 'touchstart') return;
            if (window.innerWidth < 768) return;
            if (props.fullscreenOnTablet && window.innerWidth < 1024) return;
            isDragging.value = true;
            dragStart.value = {
                x: event.clientX - position.value.x,
                y: event.clientY - position.value.y,
            };

            // Capture the modal's initial top position for bounds calculation
            if (modalRef.value) {
                const rect = modalRef.value.getBoundingClientRect();
                initialModalTop.value = rect.top - position.value.y;
            }

            document.addEventListener('mousemove', onDrag);
            document.addEventListener('mouseup', stopDrag);
        }

        // Drag the modal
        function onDrag(event) {
            if (!isDragging.value) return;

            let newX = event.clientX - dragStart.value.x;
            let newY = event.clientY - dragStart.value.y;

            // Get viewport dimensions
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // Minimum visible portion for sides/bottom
            const minVisible = 100;
            // Small margin from top of screen
            const topMargin = 10;

            // Constrain horizontal movement - keep at least minVisible pixels on screen
            const maxLeft = -(viewportWidth / 2 - minVisible);
            const maxRight = viewportWidth / 2 - minVisible;
            newX = Math.max(maxLeft, Math.min(maxRight, newX));

            // Constrain vertical movement
            // Top: Don't let the modal's top edge go above the top of the viewport
            const maxUp = -(initialModalTop.value - topMargin);
            // Bottom: Keep at least minVisible pixels on screen
            const maxDown = viewportHeight / 2 - minVisible;
            newY = Math.max(maxUp, Math.min(maxDown, newY));

            position.value = { x: newX, y: newY };
        }

        // Stop dragging the modal
        function stopDrag() {
            isDragging.value = false;
            document.removeEventListener('mousemove', onDrag);
            document.removeEventListener('mouseup', stopDrag);
        }

        // Remove event listeners when the modal is unmounted
        onUnmounted(() => {
            document.removeEventListener('mousemove', onDrag);
            document.removeEventListener('mouseup', stopDrag);
        });

        return {
            hasHeaderSlot,
            hasCustomHeaderSlot,
            closeModal,
            handleCancel,
            handleDialogClose,
            removeItem,
            defaultFocus,
            modalRef,
            emit,

            // Retention
            unmountWhenHidden: retained.unmountWhenHidden,
            contentKey: retained.contentKey,
            confirmOpen: retained.confirmOpen,
            handleDismiss,
            handleCloseButton,
            keepEditing: retained.keepEditing,
            confirmDiscard: retained.confirmDiscard,
            afterLeave: retained.afterLeave,

            // Drag and drop functionality
            modalStyle,
            startDrag,
            isDragging,
        };
    },
    computed: {
        open: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit('update:modelValue', value);
            },
        },

        hasFooterSlot() {
            return !!this.$slots.footer;
        },
    },
};
</script>

<style scoped>
.muval-modal-viewport-shell {
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
}

@media (min-width: 768px) {
    .muval-modal-viewport-shell {
        height: 100%;
        max-height: none;
    }
}
</style>
